import escapeRegExp from 'lodash/escapeRegExp';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { QuickSearchPopup } from '../Search/QuickSearchPopup';
import type { SearchResult } from '../Search/Types';

type MenuItem = {
    id: number;
    url: string;
    onlyExactMatchActive: boolean;
    name: string;
    icon: string;
    pageType: string;
};

export interface IHeaderMenuProps {
    items: MenuItem[];
    searchUrl: string;
    region: string;
    activeItemId?: number;
}

export const HeaderMenu: React.FC<IHeaderMenuProps> = (props) => {
    const [activeMenuItemId, setActiveMenuItemId] = useState(props.activeItemId);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchtext, setSearchtext] = useState('');
    const [quickItems, setQuickItems] = useState<SearchResult[]>([]);
    const [hideQuickSearch, setHideQuickSearch] = useState(true);
    const [activeSearchItem, setActiveSearchItem] = useState(-1);

    const searchLink = useRef<HTMLAnchorElement>(null);
    const searchField = useRef<HTMLInputElement>(null);
    const quickTimeoutId = useRef<NodeJS.Timeout | null>(null);
    const activeHeaderItemTimer = useRef<NodeJS.Timeout | null>(null);

    const quickItemsToShow = 5;

    useEffect(() => {
        activeHeaderItemTimer.current = setInterval(evaluateActiveItem, 100);

        return () => {
            if (activeHeaderItemTimer.current) {
                clearTimeout(activeHeaderItemTimer.current);
            }
        };
    }, []);

    const evaluateActiveItem = () => {
        const url = window.location.pathname + window.location.search;
        const closestMatch = props.items.reduce(
            (prev, curr) => {
                if (curr.onlyExactMatchActive && curr.url !== url) {
                    return prev;
                }
                // Count the number of matching characters from the start of the URL
                const regex = escapeRegExp(curr.url);
                const match = url.match('^' + regex);
                if (match && match[0].length > prev.match) {
                    return { match: match[0].length, itemId: curr.id };
                }
                return prev;
            },
            { match: 0, itemId: null },
        );

        if (closestMatch.itemId) {
            setActiveMenuItemId(closestMatch.itemId);
        }
    };

    const handleFocus = () => {
        setHideQuickSearch(false);
        setSearchOpen(true);
    };

    const openSearch = () => {
        setSearchOpen(true);

        searchField.current.focus();
    };

    const closeSearch = (keepLinkFocus: boolean) => {
        if (quickTimeoutId.current) {
            clearTimeout(quickTimeoutId.current);
        }
        setSearchOpen(false);

        if (keepLinkFocus && searchLink.current) {
            searchLink.current.focus();
        }
    };

    const searchKeyDown = (event) => {
        if (event.keyCode === 13 && activeSearchItem === -1) {
            search();
        } else if (event.keyCode === 27 && searchtext.length === 0) {
            closeSearch(true);
        } else if (event.keyCode === 9 && quickItems.length == 0) {
            closeSearch(false);
        } else if (event.keyCode === 38 || (event.keyCode === 9 && event.shiftKey)) {
            event.preventDefault();
            let activeItem = activeSearchItem - 1;

            if (activeItem < -1) {
                activeItem = Math.min(quickItems.length - 1, quickItemsToShow);
            }

            setActiveSearchItem(activeItem);
        } else if (event.keyCode === 40 || event.keyCode === 9) {
            event.preventDefault();
            let activeItem = activeSearchItem + 1;

            if (activeItem > Math.min(quickItems.length - 1, quickItemsToShow)) {
                activeItem = -1;
            }

            setActiveSearchItem(activeItem);
        } else if (event.keyCode === 13) {
            const activeItem = activeSearchItem;

            if (activeItem > -1) {
                const searchItem = quickItems[activeItem];

                window.location.href = searchItem.url;
            }
        }
    };

    const quickSearch = (query: string) => {
        fetch('/api/Search/action/QuickSearch/?query=' + query + '&region=' + props.region)
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
            })
            .then((data: SearchResult[]) => {
                setQuickItems(data);
                setActiveSearchItem(-1);
                setHideQuickSearch(false);
            });
    };

    const deferredQuickSearch = (query: string) => {
        if (quickTimeoutId.current) {
            clearTimeout(quickTimeoutId.current);
        }

        if (query.length === 0) {
            setQuickItems([]);
            return;
        }

        quickTimeoutId.current = setTimeout(() => {
            quickSearch(query);
        }, 500);
    };

    const handleChange = (event) => {
        setSearchtext(event.target.value);
        deferredQuickSearch(event.target.value);
    };

    const search = () => {
        if (!props.searchUrl) {
            return;
        }

        window.location.href = getQuerySearchUrl();
    };

    const getQuerySearchUrl = () => {
        return props.searchUrl + '?query=' + encodeURI(searchtext);
    };

    const handleSearchClick = (event) => {
        if (event.detail === 0) {
            // This is a keyboard event, continue with default action
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        openSearch();
    };

    const renderItem = (item: MenuItem): JSX.Element => {
        if (item.pageType === 'SearchPage') {
            return renderSearch(item);
        }

        const labelId = `label-${item.id}`;
        return (
            <a
                key={item.id}
                href={item.url}
                className={'header__menu-item' + (item.id === activeMenuItemId ? ' header__menu-item--active' : '')}
                aria-labelledby={labelId}
            >
                <div className="header__navigation-icon">
                    <Icon symbol={item.icon} />
                </div>
                <div className="header__navigation-text">
                    <div className="header__navigation-text-arrow" />
                    <div id={labelId} className="header__navigation-text-wrapper">
                        {item.name}
                    </div>
                </div>
            </a>
        );
    };

    const renderSearch = (item: MenuItem) => {
        if (!props.searchUrl) {
            return null;
        }

        return (
            <a
                key={item.id}
                ref={searchLink}
                aria-hidden="true"
                href={item.url}
                className={'header__navigation-search-item header__menu-item' + (item.id === activeMenuItemId ? ' header__menu-item--active' : '')}
                tabIndex={-1}
                onClick={handleSearchClick}
            >
                <div className="header__navigation-icon">
                    <Icon symbol="icon-search" />
                </div>
                <div className="header__navigation-text">
                    <div className="header__navigation-text-arrow" />
                    <div className="header__navigation-text-wrapper">{item.name}</div>
                </div>
            </a>
        );
    };

    const items = props.items.map(renderItem);

    return (
        <div className="header__navigation">
            <nav className={searchOpen ? 'header__menu-area' : 'header__menu-area header__menu-area--open'}>{items}</nav>
            <div className={searchOpen ? 'header__search-area header__search-area--open' : 'header__search-area'}>
                <input
                    ref={searchField}
                    aria-label="Sök"
                    type="search"
                    placeholder="Sök"
                    value={searchtext}
                    onKeyDown={searchKeyDown}
                    onChange={handleChange}
                    //onBlur={handleBlur}
                    onFocus={handleFocus}
                />
                <i className="icon-search icon-flip-horizontal" aria-hidden="true" onClick={search} />
                <div className="header__search-area-quicksearch">
                    <QuickSearchPopup
                        querySearchUrl={getQuerySearchUrl()}
                        activeItem={activeSearchItem}
                        items={quickItems}
                        searchtext={searchtext}
                        hide={hideQuickSearch}
                        numberOfItemsToShow={quickItemsToShow}
                    />
                </div>
            </div>
            {searchOpen ? (
                <a href="#" className="header__navigation-close-item" onClick={() => closeSearch(false)}>
                    <i className="icon-cancel" aria-hidden="true" />
                </a>
            ) : null}
        </div>
    );
};
